/**
 * Roles & Responsibilities Overview Page
 * 
 * World-class UI/UX for displaying:
 * - Role cards with filtering
 * - RACI Matrix (derived from role responsibilities)
 * - Coverage Model
 * 
 * All data is driven by the consolidated Markdown registry.
 */

import React, { useState, useMemo } from 'react';
import { usePrint } from '../../hooks/usePrint';
import PrintButton from '../../components/PrintButton';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  Search,
  ChevronRight,
  Shield,
  Briefcase,
  UserCog,
  Calculator,
  Truck,
  Megaphone,
  Database,
  Building2,
  Phone,
  CheckCircle,
  Crown,
  Car,
  Headphones,
  UserCheck,
  Target,
  ClipboardList,
  Grid3X3,
  LayoutGrid
} from 'lucide-react';
import { BrandSelector } from '../../components/pipeline/BrandSelector';
import {
  getRolesRegistry,
  getAllRoles,
  getRoleGroups,
  listRoles,
  computeRACIMatrix,
  getCoverageModel,
  getRoleCounts,
} from '../../lib/rolesRegistry';
import { getAllStages } from '../../lib/leadPipelineRegistry';
import type { 
  PipelineBrand, 
  RoleGroup,
  RACIType,
} from '../../types';

// =============================================================================
// Icon Mapping
// =============================================================================

const iconMap: Record<string, React.ElementType> = {
  Headphones,
  Users,
  Calculator,
  Truck,
  Megaphone,
  Database,
  Building2,
  Phone,
  CheckCircle,
  UserCog,
  UserCheck,
  Crown,
  Car,
  Search,
  Shield,
  Briefcase,
  Target,
  ClipboardList,
};

function getIcon(iconName?: string): React.ElementType {
  if (!iconName) return Users;
  return iconMap[iconName] || Users;
}

// =============================================================================
// Sub-Components
// =============================================================================

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'purple' | 'amber' | 'red' | 'cyan' | 'pink';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    cyan: 'bg-cyan-50 text-cyan-600 border-cyan-200',
    pink: 'bg-pink-50 text-pink-600 border-pink-200',
  };

  return (
    <div className={`rounded-xl p-4 border ${colorClasses[color]} transition-all hover:shadow-md`}>
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-white/80">
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm opacity-80">{label}</p>
        </div>
      </div>
    </div>
  );
};

// RACI Badge Component
const RACIBadge: React.FC<{ value: RACIType | null; size?: 'sm' | 'md' }> = ({ value, size = 'md' }) => {
  if (!value) return <span className="text-gray-300">-</span>;
  
  const colors: Record<RACIType, string> = {
    R: 'bg-blue-500 text-white',
    A: 'bg-purple-500 text-white',
    C: 'bg-amber-500 text-white',
    I: 'bg-gray-400 text-white',
  };
  
  const labels: Record<RACIType, string> = {
    R: 'Responsible',
    A: 'Accountable',
    C: 'Consulted',
    I: 'Informed',
  };
  
  const sizeClasses = size === 'sm' 
    ? 'w-6 h-6 text-xs' 
    : 'w-8 h-8 text-sm';
  
  return (
    <span 
      className={`inline-flex items-center justify-center ${sizeClasses} rounded-full font-bold ${colors[value]}`}
      title={labels[value]}
    >
      {value}
    </span>
  );
};

// =============================================================================
// Main Component
// =============================================================================

const RolesOverview: React.FC = () => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const { printRef, handlePrint } = usePrint('Almajdouie Motors – Roles & Responsibilities');
  
  // State
  const [selectedBrand, setSelectedBrand] = useState<PipelineBrand>('Changan');
  const [activeTab, setActiveTab] = useState<string>('roles');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<RoleGroup | 'all'>('all');
  const [selectedStage, setSelectedStage] = useState<string>('all');
  
  // Get data from registry
  const registry = getRolesRegistry();
  const roleGroups = getRoleGroups();
  const counts = getRoleCounts();
  const stages = getAllStages();
  const raciMatrix = useMemo(() => computeRACIMatrix(), []);
  const coverageModel = getCoverageModel();
  
  // Filter roles
  const filteredRoles = useMemo(() => {
    return listRoles({
      brand: selectedBrand,
      group: selectedGroup === 'all' ? undefined : selectedGroup,
      stageId: selectedStage === 'all' ? undefined : selectedStage,
      search: searchTerm || undefined,
    });
  }, [selectedBrand, selectedGroup, selectedStage, searchTerm]);
  
  // Tab configuration
  const tabs = [
    { id: 'roles', label: isArabic ? 'الأدوار' : 'Roles', icon: Users, count: counts.totalRoles },
    { id: 'raci', label: isArabic ? 'مصفوفة RACI' : 'RACI Matrix', icon: Grid3X3, count: null },
    { id: 'coverage', label: isArabic ? 'نموذج التغطية' : 'Coverage Model', icon: LayoutGrid, count: null },
  ];

  return (
    <div ref={printRef} className="space-y-8">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Users className="w-6 h-6" />
                </div>
                <span className="text-white/80 text-sm font-medium uppercase tracking-wider">
                  {isArabic ? 'الأدوار والمسؤوليات' : 'Organizational Structure'}
                </span>
              </div>
              <h1 className="text-4xl font-bold mb-3">
                {isArabic ? 'الأدوار والمسؤوليات' : 'Roles & Responsibilities'}
              </h1>
              <p className="text-white/80 max-w-2xl text-lg">
                {isArabic 
                  ? registry.descriptionAr || 'تعريفات الأدوار ومصفوفة المسؤوليات ونموذج التغطية لجميع الفرق'
                  : registry.description || 'Role definitions, RACI matrix, and coverage model for all teams'
                }
              </p>
            </div>
            
            {/* Brand Selector + Print */}
            <div className="flex-shrink-0 flex flex-col gap-3 items-end">
              <PrintButton
                onClick={handlePrint}
                isAr={isArabic}
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              />
              <BrandSelector 
                selectedBrand={selectedBrand} 
                onBrandChange={setSelectedBrand}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard 
          label={isArabic ? 'إجمالي الأدوار' : 'Total Roles'}
          value={counts.totalRoles}
          icon={<Users className="w-5 h-5" />}
          color="blue"
        />
        {counts.byGroup.slice(0, 4).map((g, idx) => {
          const group = roleGroups.find(rg => rg.id === g.group);
          const colors: ('green' | 'purple' | 'amber' | 'cyan')[] = ['green', 'purple', 'amber', 'cyan'];
          const GroupIcon = getIcon(group?.icon);
          return (
            <StatCard 
              key={g.group}
              label={isArabic ? group?.labelAr || g.group : group?.label || g.group}
              value={g.count}
              icon={<GroupIcon className="w-5 h-5" />}
              color={colors[idx % colors.length]}
            />
          );
        })}
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col lg:flex-row gap-4 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={isArabic ? 'البحث في الأدوار...' : 'Search roles...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
          />
        </div>
        
        {/* Group Filter */}
        <select
          value={selectedGroup}
          onChange={(e) => setSelectedGroup(e.target.value as RoleGroup | 'all')}
          className="px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent min-w-[180px]"
        >
          <option value="all">{isArabic ? 'جميع المجموعات' : 'All Groups'}</option>
          {roleGroups.map(group => (
            <option key={group.id} value={group.id}>
              {isArabic ? group.labelAr : group.label}
            </option>
          ))}
        </select>
        
        {/* Stage Filter */}
        <select
          value={selectedStage}
          onChange={(e) => setSelectedStage(e.target.value)}
          className="px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent min-w-[200px]"
        >
          <option value="all">{isArabic ? 'جميع المراحل' : 'All Stages'}</option>
          {stages.map(stage => (
            <option key={stage.meta.id} value={stage.meta.id}>
              {isArabic ? stage.meta.titleAr : stage.meta.title}
            </option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8 overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
                {tab.count !== null && (
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id 
                      ? 'bg-emerald-100 text-emerald-600' 
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Roles Tab */}
        {activeTab === 'roles' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRoles.map(role => {
              const group = roleGroups.find(g => g.id === role.group);
              const RoleIcon = getIcon(role.icon || group?.icon);
              const primaryStages = role.responsibilitiesByStage
                .filter(r => r.responsibilityType === 'R')
                .map(r => stages.find(s => s.meta.id === r.stageId)?.meta.title)
                .filter(Boolean)
                .slice(0, 3);
              
              return (
                <Link
                  key={role.id}
                  to={`/roles/${role.id}`}
                  className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg hover:border-emerald-300 dark:hover:border-emerald-600 transition-all"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div 
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: `${role.color || group?.color || '#10b981'}20` }}
                    >
                      <RoleIcon 
                        className="w-6 h-6" 
                        style={{ color: role.color || group?.color || '#10b981' }}
                      />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-emerald-600 transition-colors truncate">
                          {isArabic ? role.titleAr : role.title}
                        </h3>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" />
                      </div>
                      
                      {/* Group Badge */}
                      <span 
                        className="inline-block px-2 py-0.5 rounded-full text-xs font-medium mb-2"
                        style={{ 
                          backgroundColor: `${group?.color || '#10b981'}20`,
                          color: group?.color || '#10b981'
                        }}
                      >
                        {isArabic ? group?.labelAr : group?.label}
                      </span>
                      
                      {/* Reports To */}
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                        {isArabic ? 'يقدم التقارير إلى:' : 'Reports to:'}{' '}
                        <span className="text-gray-700 dark:text-gray-300">
                          {isArabic ? role.reportsToAr : role.reportsTo}
                        </span>
                      </p>
                      
                      {/* Primary Stages */}
                      {primaryStages.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {primaryStages.map((stageName, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-xs"
                            >
                              {stageName}
                            </span>
                          ))}
                          {role.responsibilitiesByStage.filter(r => r.responsibilityType === 'R').length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 rounded text-xs">
                              +{role.responsibilitiesByStage.filter(r => r.responsibilityType === 'R').length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
            
            {filteredRoles.length === 0 && (
              <div className="col-span-full text-center py-12 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{isArabic ? 'لم يتم العثور على أدوار تطابق معايير البحث' : 'No roles found matching your criteria'}</p>
              </div>
            )}
          </div>
        )}

        {/* RACI Matrix Tab */}
        {activeTab === 'raci' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Legend */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <div className="flex flex-wrap gap-4 items-center">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {isArabic ? 'مفتاح:' : 'Legend:'}
                </span>
                <div className="flex items-center gap-2">
                  <RACIBadge value="R" size="sm" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {isArabic ? 'مسؤول (ينفذ العمل)' : 'Responsible (does the work)'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <RACIBadge value="A" size="sm" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {isArabic ? 'مسؤول أعلى (يتحمل المسؤولية)' : 'Accountable (ultimately answerable)'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <RACIBadge value="C" size="sm" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {isArabic ? 'مستشار (يقدم مدخلات)' : 'Consulted (provides input)'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <RACIBadge value="I" size="sm" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {isArabic ? 'مُطلع (يُبقى على علم)' : 'Informed (kept in the loop)'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Matrix Table */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 sticky left-0 bg-gray-50 dark:bg-gray-900 z-10">
                      {isArabic ? 'المرحلة' : 'Stage'}
                    </th>
                    {raciMatrix.roles.map(role => {
                      const group = roleGroups.find(g => g.id === role.group);
                      return (
                        <th 
                          key={role.id}
                          className="px-3 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700"
                        >
                          <Link 
                            to={`/roles/${role.id}`}
                            className="hover:text-emerald-600 transition-colors"
                          >
                            <div className="flex flex-col items-center gap-1">
                              <span 
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: group?.color || '#10b981' }}
                              />
                              <span className="whitespace-nowrap">
                                {isArabic ? role.titleAr : role.title}
                              </span>
                            </div>
                          </Link>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {raciMatrix.rows.map((row, idx) => (
                    <tr 
                      key={row.stageId}
                      className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 sticky left-0 bg-inherit z-10">
                        <Link 
                          to={`/pipeline/stage/${row.stageId}`}
                          className="hover:text-emerald-600 transition-colors flex items-center gap-2"
                        >
                          <span className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-bold">
                            {row.stageOrder}
                          </span>
                          {isArabic ? row.stageNameAr : row.stageName}
                        </Link>
                      </td>
                      {row.cells.map(cell => (
                        <td 
                          key={cell.roleId}
                          className="px-3 py-3 text-center border-b border-gray-200 dark:border-gray-700"
                        >
                          <RACIBadge value={cell.value} size="sm" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Coverage Model Tab */}
        {activeTab === 'coverage' && (
          <div className="space-y-6">
            {/* Staffing Requirements */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-600" />
                {isArabic ? 'متطلبات التوظيف لكل فرع' : 'Staffing Requirements per Branch'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {coverageModel.rolesPerBranch.map(item => {
                  const role = getAllRoles().find(r => r.id === item.roleId);
                  const group = role ? roleGroups.find(g => g.id === role.group) : null;
                  const RoleIcon = role ? getIcon(role.icon || group?.icon) : Users;
                  
                  return (
                    <div 
                      key={item.roleId}
                      className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-emerald-300 transition-colors"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div 
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: `${group?.color || '#10b981'}20` }}
                        >
                          <RoleIcon 
                            className="w-5 h-5"
                            style={{ color: group?.color || '#10b981' }}
                          />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {isArabic ? role?.titleAr : role?.title || item.roleId}
                          </p>
                        </div>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">{isArabic ? 'الحد الأدنى:' : 'Minimum:'}</span>
                        <span className="font-semibold text-amber-600">{item.minimumCount}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">{isArabic ? 'الموصى به:' : 'Recommended:'}</span>
                        <span className="font-semibold text-emerald-600">{item.recommendedCount}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Minimum Staffing Policy */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-200 dark:border-amber-800 p-6">
              <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-300 mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                {isArabic ? 'سياسة الحد الأدنى للتوظيف' : 'Minimum Staffing Policy'}
              </h3>
              <p className="text-amber-700 dark:text-amber-400">
                {isArabic ? coverageModel.minimumStaffingAr : coverageModel.minimumStaffing}
              </p>
            </div>

            {/* Backup Rules */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <UserCog className="w-5 h-5 text-emerald-600" />
                {isArabic ? 'قواعد الدعم والتغطية' : 'Backup & Coverage Rules'}
              </h3>
              <ul className="space-y-3">
                {(isArabic ? coverageModel.backupRulesAr : coverageModel.backupRules).map((rule, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Version Info */}
      <div className="text-center text-sm text-gray-500 dark:text-gray-400">
        {isArabic ? 'الإصدار' : 'Version'} {registry.version} | 
        {isArabic ? ' آخر تحديث: ' : ' Last Updated: '}{registry.lastUpdated}
      </div>
    </div>
  );
};

export default RolesOverview;
